// @flow
/** @jsx jsx */

import { Fragment } from 'react';
import { partial, partialRight } from 'lodash';

import { jsx } from '@emotion/core';
import {
  type ApiKeys,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { GridLayout, MessageBar, Modal, Text } from '@zapier/common-components';

import NoPaymentInformation from './NoPaymentInformation';
import PaymentMethodBank from './PaymentMethodBank';
import PaymentMethodCard from './PaymentMethodCard';
import PaymentMethodPaypal from './PaymentMethodPaypal';

export type Props = {
  /** API keys for payment backends */
  apiKeys: ApiKeys,
  /** Current payment method */
  currentMethod: ?PaymentSourceMethod,
  /** Edit payment methods in a modal */
  editInModal: boolean,
  /** Error string  */
  error: ?string,
  /** Force user to update card details */
  forceCardUpdate: boolean,
  /** User has a saved payment source */
  hasPaymentSource: boolean,
  /** User is currently editing a payment method */
  isEditing: boolean,
  /** Current payment method editor is loading */
  isEditingInFlight: boolean,
  /** Call when users cancels edit */
  onCancelEdit: () => void,
  /** Call when user edits a value */
  onEdit: PaymentSourceMethod => void,
  /** Call when edit causes an error */
  onErrorEdit: (error: Error, method: PaymentSourceMethod) => void,
  /** Call to hide change success message */
  onHideSuccessMessage: () => void,
  /** Call when to save edit */
  onSaveEdit: ({}, PaymentSourceMethod) => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod, hasSwitched: boolean) => void,
  /** Current saved payment method */
  savedSource: ?PaymentSource,
  /** Show payment changed successfully message */
  showChangeSuccessMessage: boolean,
  /** User's supported payment methods */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const renderPaymentMethod = (props: Props) => {
  const commonProps = {
    apiKeys: props.apiKeys,
    currentMethod: props.currentMethod,
    hideSwitch: props.supportedMethods.length < 2,
    isEditing: !props.editInModal && props.isEditing,
    onCancelEdit: props.onCancelEdit,
    onEdit: partial(props.onEdit, props.currentMethod),
    onErrorEdit: partialRight(props.onErrorEdit, props.currentMethod),
    onSaveEdit: props.onSaveEdit,
    onSelectSource: partialRight(props.onSelectSource, true),
    savedSource:
      props.savedSource && props.savedSource.type === props.currentMethod
        ? props.savedSource
        : null,
    // Remove this when bank transfer support ships
    showSelect: props.supportedMethods.length > 2,
    supportedMethods: props.supportedMethods,
  };

  switch (props.currentMethod) {
    case paymentSourceMethods.card:
      return (
        <PaymentMethodCard
          {...commonProps}
          error={props.error}
          forceCardUpdate={props.forceCardUpdate}
          isEditingInFlight={props.isEditingInFlight}
        />
      );
    case paymentSourceMethods.paypal:
      return <PaymentMethodPaypal {...commonProps} />;
    case paymentSourceMethods.bank:
      return (
        <PaymentMethodBank
          {...commonProps}
          error={props.error}
          isEditingInFlight={props.isEditingInFlight}
        />
      );
    default:
      // This prevents the AsyncOverlay from blowing up because of null
      // children
      return <div />;
  }
};

const renderPaymentSection = (props: Props) => {
  const showPaymentMethod = props.hasPaymentSource || props.isEditing;

  return showPaymentMethod ? (
    <Fragment>
      {props.showChangeSuccessMessage && (
        <MessageBar
          margin={{ bottom: 10 }}
          onDismiss={props.onHideSuccessMessage}
          type="success"
        >
          Changes have been saved
        </MessageBar>
      )}
      {renderPaymentMethod(props)}
    </Fragment>
  ) : (
    <NoPaymentInformation
      currentMethod={props.currentMethod}
      onSelectSource={props.onSelectSource}
      supportedMethods={props.supportedMethods}
    />
  );
};

const PaymentMethodsSection = (props: Props) => {
  return (
    <Fragment>
      {renderPaymentSection(props)}
      {props.isEditing && props.editInModal && (
        <Modal onClose={props.onCancelEdit} padding={20}>
          <GridLayout gap={20}>
            <Text type="main-head">Edit Payment Details</Text>
            {renderPaymentSection({ ...props, editInModal: false })}
          </GridLayout>
        </Modal>
      )}
    </Fragment>
  );
};

export default PaymentMethodsSection;
